import { useEffect, useRef } from 'react';

import { useFormContext } from '#/components/Form/FormContext';
import { RichError } from '#/utils/RichError';
import { trackError } from '#/utils/trackError';

import { getAllFieldNamesFromSteps } from './helpers/getAllFieldNamesFromSteps';
import { getErrorRootName } from './helpers/getErrorRootName';
import type { MultiStepFormStep } from './MultiStepFormTypes';

type Props = {
  steps: MultiStepFormStep[];
  formTitle?: string;
};

/**
 * Tracks validation errors that don't map to any form field.
 * NOTE: Uses useEffect (not useFormSubmittedInterceptor) since
 * the interceptor fires *before* state updates.
 */
export function useTrackUnmappedErrors({ steps, formTitle }: Props) {
  const { errors } = useFormContext();
  const prevErrorKeysRef = useRef<string>('');

  useEffect(() => {
    if (!errors) return;

    const errorKeys = Object.keys(errors);
    const errorKeysStr = errorKeys.toSorted().join(',');

    // Only track when errors change (not on every render)
    if (errorKeysStr === prevErrorKeysRef.current) return;
    prevErrorKeysRef.current = errorKeysStr;

    if (errorKeys.length === 0) return;

    const knownFields = getAllFieldNamesFromSteps(steps);
    const unmappedErrors = errorKeys
      .filter((k) => k !== 'general' && k !== '')
      .filter((err) => !knownFields.has(getErrorRootName(err)));

    if (unmappedErrors.length > 0) {
      trackError(
        new RichError('Unmapped validation errors', {
          context: 'MultiStepForm',
          unmappedFields: unmappedErrors,
          formTitle,
        }),
      );
    }
  }, [errors, steps, formTitle]);
}
